/**
 * hooks/useCrudApi.js
 * Bridges API layer and CrudContext.
 * Called once in FormsListApp — all side-effects live here.
 */

import { useEffect, useCallback } from "@wordpress/element";
import {
    getForms,
    duplicateForm,
    toggleFormStatus,
    trashForm,
    restoreForm,
    permanentDeleteForm,
    bulkTrashForms,
    bulkRestoreForms,
    bulkPermanentDeleteForms,
    bulkUpdateStatus,
} from "../api/forms";
import {
    ACTIONS,
    useCrudListState,
    useCrudListDispatch,
} from "../context/CrudContext";

const useCrudApi = () => {
    const dispatch = useCrudListDispatch();
    const { page, perPage, search, status } = useCrudListState();

    // ── Fetch ─────────────────────────────────────────────────────────────────

    const fetchForms = useCallback(async () => {
        dispatch({ type: ACTIONS.FETCH_START });
        try {
            const data = await getForms({
                page,
                per_page: perPage,
                search,
                status,
            });
            dispatch({ type: ACTIONS.FETCH_SUCCESS, payload: data });
        } catch (err) {
            dispatch({
                type: ACTIONS.FETCH_ERROR,
                payload: err?.message ?? "Failed to load forms.",
            });
        }
    }, [dispatch, page, perPage, search, status]);

    useEffect(() => {
        fetchForms();
    }, [fetchForms]);

    // ── Toggle publish / draft ────────────────────────────────────────────────

    const handleToggleStatus = useCallback(
        async (form) => {
            const next = form.status === "publish" ? "draft" : "publish";
            dispatch({
                type: ACTIONS.UPDATE_ITEM,
                payload: { id: form.id, changes: { status: next } },
            });
            try {
                await toggleFormStatus(form.id, form.status);
            } catch {
                dispatch({
                    type: ACTIONS.UPDATE_ITEM,
                    payload: { id: form.id, changes: { status: form.status } },
                });
            }
        },
        [dispatch],
    );

    // ── Duplicate ─────────────────────────────────────────────────────────────

    const handleDuplicate = useCallback(
        async (form) => {
            try {
                await duplicateForm(form.id);
                await fetchForms();
            } catch (err) {
                console.error("Duplicate failed:", err);
            }
        },
        [fetchForms],
    );

    // ── Move to Trash ─────────────────────────────────────────────────────────

    const handleTrash = useCallback(
        async (id) => {
            dispatch({ type: ACTIONS.REMOVE_ITEM, payload: id });
            try {
                await trashForm(id);
            } catch {
                await fetchForms();
            }
        },
        [dispatch, fetchForms],
    );

    // ── Restore ───────────────────────────────────────────────────────────────

    const handleRestore = useCallback(
        async (id) => {
            dispatch({ type: ACTIONS.REMOVE_ITEM, payload: id });
            try {
                await restoreForm(id);
            } catch {
                await fetchForms();
            }
        },
        [dispatch, fetchForms],
    );

    // ── Permanent Delete ──────────────────────────────────────────────────────

    const handlePermanentDelete = useCallback(
        async (id) => {
            dispatch({ type: ACTIONS.REMOVE_ITEM, payload: id });
            try {
                await permanentDeleteForm(id);
            } catch {
                await fetchForms();
            }
        },
        [dispatch, fetchForms],
    );

    // ── Bulk: trash ───────────────────────────────────────────────────────────

    const handleBulkTrash = useCallback(
        async (ids) => {
            dispatch({ type: ACTIONS.REMOVE_ITEMS, payload: ids });
            try {
                await bulkTrashForms(ids);
            } catch {
                await fetchForms();
            }
        },
        [dispatch, fetchForms],
    );

    // ── Bulk: restore ─────────────────────────────────────────────────────────

    const handleBulkRestore = useCallback(
        async (ids) => {
            dispatch({ type: ACTIONS.REMOVE_ITEMS, payload: ids });
            try {
                await bulkRestoreForms(ids);
            } catch {
                await fetchForms();
            }
        },
        [dispatch, fetchForms],
    );

    // ── Bulk: permanent delete ────────────────────────────────────────────────

    const handleBulkPermanentDelete = useCallback(
        async (ids) => {
            dispatch({ type: ACTIONS.REMOVE_ITEMS, payload: ids });
            try {
                await bulkPermanentDeleteForms(ids);
            } catch {
                await fetchForms();
            }
        },
        [dispatch, fetchForms],
    );

    // ── Bulk: publish / draft ─────────────────────────────────────────────────

    const handleBulkStatus = useCallback(
        async (ids, nextStatus) => {
            ids.forEach((id) =>
                dispatch({
                    type: ACTIONS.UPDATE_ITEM,
                    payload: { id, changes: { status: nextStatus } },
                }),
            );
            dispatch({ type: ACTIONS.CLEAR_SELECTION });
            try {
                await bulkUpdateStatus(ids, nextStatus);
            } catch {
                await fetchForms();
            }
        },
        [dispatch, fetchForms],
    );

    return {
        refetch: fetchForms,
        handleToggleStatus,
        handleDuplicate,
        handleTrash,
        handleRestore,
        handlePermanentDelete,
        handleBulkTrash,
        handleBulkRestore,
        handleBulkPermanentDelete,
        handleBulkStatus,
    };
};

export default useCrudApi;
